import { Flex, HStack, Stack, useToast } from "@chakra-ui/react";
import * as yup from "yup";
import apiClient from "services/ApiClient";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { generatePath } from "react-router";
import { AdminCompany } from "models/company";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ProceedCancelButtons from "components/Button/ProceedCancelButtons";
import { FloatingField } from "design-system";
import { companyKeys } from "features/Company/hooks";

export const COMPANY_EMAIL_DEFAULT_FROM = {
  label: "Email 'From Name' (example 'Camino Onboarding')",
  name: "customEmailDefaultFrom",
};
export const COMPANY_EMAIL_DEFAULT_REPLY_TO = {
  label: "Email 'Reply To'",
  name: "customEmailDefaultReplyTo",
};

export interface EmailDefaultUpdateFormValues {
  customEmailDefaultFrom?: string;
  customEmailDefaultReplyTo?: string;
}

export const emailDefaultSchema = yup.object({
  customEmailDefaultFrom: yup.string().label(COMPANY_EMAIL_DEFAULT_FROM.label).notRequired(),
  customEmailDefaultReplyTo: yup.string().label(COMPANY_EMAIL_DEFAULT_REPLY_TO.label).notRequired(),
});

const values = (company: AdminCompany | undefined) => ({
  customEmailDefaultFrom: company?.customEmailDefaultFrom || null,
  customEmailDefaultReplyTo: company?.customEmailDefaultReplyTo || null,
});

interface EmailSettingsFormProps {
  company: AdminCompany;
  isLoading: boolean;
  onClose: () => void;
}

export function EmailSettingsForm({ company, isLoading, onClose }: EmailSettingsFormProps) {
  const queryClient = useQueryClient();
  const toast = useToast({
    status: "success",
  });
  const defaultValues = useMemo(() => values(company), [company]);

  const {
    control,
    formState: { errors, dirtyFields, isDirty },
    handleSubmit,
    reset,
    setFocus,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(emailDefaultSchema),
    defaultValues,
  });

  useEffect(() => {
    setFocus(COMPANY_EMAIL_DEFAULT_FROM.name as keyof EmailDefaultUpdateFormValues);
  }, [setFocus]);

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  /* HandleOnSubmit */
  const { mutate: onSubmit, isPending: submitIsLoading } = useMutation({
    mutationFn: (data: EmailDefaultUpdateFormValues) => {
      const updateData = Object.fromEntries(
        Object.entries(data).filter(([key]) => dirtyFields[key as keyof EmailDefaultUpdateFormValues] === true),
      );
      return apiClient.put<Company>(generatePath(API_ROUTES.companies.show, { id: "current" }), {
        company: updateData,
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.current() });
      toast({
        title: "Success!",
      });
    },
    onError: (mutateError) => {
      console.log("userCreateError", mutateError);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });

  return (
    <Flex direction="column">
      <Stack>
        <FloatingField name={COMPANY_EMAIL_DEFAULT_FROM.name} errors={errors.customEmailDefaultFrom?.message}>
          <FloatingField.Label>{COMPANY_EMAIL_DEFAULT_FROM.label}</FloatingField.Label>
          <FloatingField.Text control={control} name={COMPANY_EMAIL_DEFAULT_FROM.name} disabled={isLoading} />
        </FloatingField>
        <FloatingField name={COMPANY_EMAIL_DEFAULT_REPLY_TO.name} errors={errors.customEmailDefaultReplyTo?.message}>
          <FloatingField.Label>{COMPANY_EMAIL_DEFAULT_REPLY_TO.label}</FloatingField.Label>
          <FloatingField.Text control={control} name={COMPANY_EMAIL_DEFAULT_REPLY_TO.name} disabled={isLoading} />
        </FloatingField>
      </Stack>
      <HStack mt={12} justify="end">
        <ProceedCancelButtons
          handleSubmit={handleSubmit(onSubmit)}
          onClose={onClose}
          proceedText="Update"
          isLoading={submitIsLoading}
          proceedDisabled={!isDirty}
        />
      </HStack>
    </Flex>
  );
}
